import { submitFormJson } from 'widgets/toolbox/ajax';
import { appendParamsToUrl } from 'widgets/toolbox/util';
import { scrollWindowTo } from 'widgets/toolbox/scroll';

import { TInputCheckboxInstance } from 'widgets/forms/InputCheckbox';
import { TShippingFormInstance } from 'widgets/checkout/ShippingForm';
import { TBillingFormInstance } from 'widgets/checkout/BillingForm';
import { TProcessButtonInstance } from 'widgets/global/ProcessButton';

/* import { TPaymentResultData } from 'widgets/checkout/CheckoutMgr';
 */import { TPaymentResult } from 'widgets/checkout/CheckoutMgr';

export type TSummarySubmissionResult = TServerResponse & {
    continueUrl: string;
    orderNo: string;
    orderToken: string;
    errorStage?: {
        stage: 'shipping' | 'billing' | 'payment';
    };
};
export type TSummarySubmissionError = TResponseError & {
    errorStage: {
        stage: 'shipping' | 'billing' | 'payment';
    };
};

/**
 * @description Checkout Summary step logic
 * @param CheckoutMgr Base widget for extending
 */
function SummaryStepMixinClassCreator(CheckoutMgr: import('widgets/checkout/CheckoutMgr').TCheckoutMgr) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class SummaryStepMixin
     * @augments CheckoutMgr
     * @classdesc Represents summary step component with the next features:
     * 1. Manage Summary step state on the checkout
     * 2. Activate/Deactivate Summary step
     * 3. Submit Summary step
     */
    class SummaryStepMixin extends CheckoutMgr {
        /**
         * @description Activate summary step
         * @emits "checkout.summary.step.activated"
         */
        summaryActivate() {
            this.eventBus().emit('checkout.summary.step.activated', this);
        }

        /**
         * @description Deactivate summary step
         */
        summaryDeActivate() {
            this.ref('summaryError').hide();
        }

        /**
         * @description Returns privacy checkbox
         * @returns Privacy checkbox
         */
        getPrivacyCheckbox(): TInputCheckboxInstance {
            let agreeToPrivacyCheckbox;

            this.getByIdDeep('agreeToPrivacy', (agreeToPrivacy: TInputCheckboxInstance) => {
                agreeToPrivacyCheckbox = agreeToPrivacy;
            });

            return agreeToPrivacyCheckbox;
        }

        /**
         * @description Submit summary form
         * @param additionalData - additional data with "Place Order" button for disabling/enabling
         * @param skipPrivacyValidation - Allows skip privacy validation if needed(e.g. PayPal)
         */
        summarySubmit(additionalData: Array<TProcessButtonInstance>, skipPrivacyValidation?: boolean) {
            if (!this.validatePlaceOrderForm(skipPrivacyValidation) || this.prefs().processingRequest) {
                return;
            }

            const submitBtn = additionalData.find(el => {
                return el?.id === 'submitButton';
            }) as TProcessButtonInstance;
            const agreeToPrivacyCheckbox = this.getPrivacyCheckbox();
            let agreeToPrivacy = 'false';

            this.ref('summaryError').hide();
            this.setPref('processingRequest', true);
            submitBtn?.startProcess();

            if (agreeToPrivacyCheckbox) {
                agreeToPrivacy = agreeToPrivacyCheckbox.getValue();
            }

            if (skipPrivacyValidation) {
                agreeToPrivacy = 'true';
            }

            submitFormJson(
                this.prefs().urlPlaceOrder,
                { agreeToPrivacy }
            ).then((response) => {
                this.handleSummarySubmissionResponse(response, submitBtn);
            }).catch(() => {
                this.setPref('processingRequest', false);
                submitBtn?.stopProcess();
            });
        }

        /**
         * @description Check summary step validity
         * @param skipPrivacyValidation Determines whether skip privacy validation or not(e.g. skips for PayPal)
         * @returns Validation result
         */
        validatePlaceOrderForm(skipPrivacyValidation?: boolean) {
            if (skipPrivacyValidation) {
                return true;
            }

            const agreeToPrivacyCheckbox = this.getPrivacyCheckbox();

            return agreeToPrivacyCheckbox ? agreeToPrivacyCheckbox.validate() : true;
        }

        /**
         * @description Handle summary submission response
         * @param response - Server response object
         * @param submitBtn - submit button for enabling
         */
        handleSummarySubmissionResponse(response: TPaymentResult, submitBtn: TProcessButtonInstance) {
            // add type check for consistency
            if (response && response.data?.redirectUrl) {
                window.location.assign(response.data.redirectUrl);
            } else if (response && !response.success) {
                this.handleSummarySubmissionError(response as (TPaymentResult), submitBtn);
            } else if (response && response.data?.continueUrl) {
                this.summarySubmitted();
                window.location.assign(
                    appendParamsToUrl(response.data.continueUrl, {
                        ID: response.data.orderNo,
                        token: response.data.orderToken
                    })
                );
            }
        }

        /**
         * @description Handle summary submission error
         * @param response - Server response object
         * @param submitBtn - submit button for enabling
         */
        handleSummarySubmissionError(response: TPaymentResult, submitBtn: TProcessButtonInstance) {
            this.setPref('processingRequest', false);
            submitBtn?.stopProcess();

            if (response.errorStage) {
                if (['billing', 'payment'].includes(response.errorStage.stage)) {
                    this.activateStepByName('billing');

                    if (response.errorMessage) {
                        this.getById(this.prefs().billingFormID, (billingFormComponent: TBillingFormInstance) => {
                            billingFormComponent.setError(response.errorMessage);
                        });
                    }
                } else if (response.errorStage.stage === 'shipping') {
                    this.activateStepByName('shipping');

                    if (response.errorMessage) {
                        this.getById(this.prefs().shippingFormID, (shippingFormComponent: TShippingFormInstance) => {
                            shippingFormComponent.setError(response.errorMessage);
                        });
                    }
                }
            } else if (response.errorMessage) {
                const summary = this.ref('summaryError');

                summary.setText(response.errorMessage).show();

                const summaryElement = summary.get();

                if (summaryElement) {
                    scrollWindowTo(summaryElement, true);
                }
            }
        }

        /**
         * @description Summary submission result handler
         */
        summarySubmitted() {
            // Placeholder for additional logic on summary submission
        }
    }

    return SummaryStepMixin;
}

export type TSummaryStepMixin = ReturnType<typeof SummaryStepMixinClassCreator>;

export type TSummaryStepMixinInstance = InstanceType<TSummaryStepMixin>;

export default SummaryStepMixinClassCreator;
